import * as tf from '@tensorflow/tfjs';
import * as faceDetection from '@tensorflow-models/face-detection';
import Image from 'image-js';
import '@tensorflow/tfjs-backend-webgl'; // Import the WebGL backend

const useImageRecognition = () => {
  const fileToImage = (file: File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img'); // Use HTMLImageElement instead of Image
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
      img.src = url;
    });
  };

  const detectFaces = async (file: File): Promise<faceDetection.Face[]> => {
    // Initialize TensorFlow.js backend
    await tf.setBackend('webgl'); // Use WebGL backend
    await tf.ready(); // Ensure the backend is ready

    // Convert the file to an image
    const imageElement = await fileToImage(file);

    let faces: faceDetection.Face[] = [];
    // Load the model (MediaPipeFaceDetector)
    if (faceDetection) {
      const model = await faceDetection.createDetector(
        faceDetection.SupportedModels.MediaPipeFaceDetector,
        {
          runtime: 'tfjs', // Use TensorFlow.js runtime
          modelType: 'short', // Use short-range model for higher accuracy
        }
      );

      // Detect faces
      faces = await model.estimateFaces(imageElement);
    }
    return faces;
  };

  const checkIfImageBlurry = async (file: File): Promise<boolean> => {
    // Convert File to ArrayBuffer using FileReader
    const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

    const image = await Image.load(arrayBuffer);

    if (image) {
      const laplacianImage = applyLaplacian(image);
      const variance = calculateVariance(laplacianImage);

      const threshold = 100;
      return variance < threshold;
    }

    return false;
  };
  // Helper function to calculate luminance
  const calculateLuminance = (r: number, g: number, b: number): number => {
    return 0.299 * r + 0.587 * g + 0.114 * b;
  };

  const calculateVariance = (imageFile: Image): number => {
    const { data } = imageFile;
    const numPixels = imageFile.width * imageFile.height;

    let sum = 0;
    let sumOfSquares = 0;

    for (let i = 0; i < data.length; i += 4) {
      const luminance = calculateLuminance(data[i], data[i + 1], data[i + 2]);
      sum += luminance;
      sumOfSquares += luminance * luminance;
    }

    const mean = sum / numPixels;
    return sumOfSquares / numPixels - mean * mean;
  };

  const applyLaplacian = (originalImage: Image): Image => {
    // Laplacian kernel
    const kernel: number[][] = [
      [-1, -1, -1],
      [-1, 8, -1],
      [-1, -1, -1],
    ];

    const { width, height } = originalImage;
    const outputImage = new Image(width, height); // Create a new blank image

    // Apply the kernel to each pixel
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let sumR = 0,
          sumG = 0,
          sumB = 0;

        for (let ky = 0; ky < 3; ky++) {
          for (let kx = 0; kx < 3; kx++) {
            const pixel = originalImage.getPixelXY(x + kx - 1, y + ky - 1); // Get neighboring pixel
            const [r, g, b] = pixel; // Extract RGB channels

            sumR += r * kernel[ky][kx];
            sumG += g * kernel[ky][kx];
            sumB += b * kernel[ky][kx];
          }
        }

        // Clamp values to the range 0-255
        sumR = Math.max(0, Math.min(255, sumR));
        sumG = Math.max(0, Math.min(255, sumG));
        sumB = Math.max(0, Math.min(255, sumB));

        // Set the clamped pixel values in the output image
        outputImage.setPixelXY(x, y, [sumR, sumG, sumB, 255]); // Include alpha (fully opaque)
      }
    }

    return outputImage;
  };

  return { checkIfImageBlurry, calculateVariance, applyLaplacian, detectFaces };
};

export default useImageRecognition;
